/**
 * What can be said about a room the visitor has not entered.
 *
 * Nothing on the way in knows anything about the room behind a code until a
 * peek answers. This is the vocabulary for what a peek finds, kept away from
 * the channel that does the finding so the words and the wire can be read apart.
 */

/** What a look at a room found, before anybody went in. */
export type RoomPeek =
  /** Nothing has been asked yet, or the code is too short to ask about. */
  | { kind: "unasked" }
  /** Asked, and waiting. The strip says nothing new while this is true. */
  | { kind: "asking" }
  /**
   * The room answered. `count` is how many are standing at its fire, and
   * `present` is who: what each member already publishes to everybody on the
   * room's channel, and nothing more.
   */
  | { kind: "answered"; count: number; present: readonly PeekMember[] }
  /** The room did not answer at all — closed, gone, or a network in the way. */
  | { kind: "silent" };

/**
 * Somebody standing in a room, as a look from outside it sees them: the name
 * and the character they published on the room's channel. Their character is
 * the emoji and the colour the wire still spells it as.
 */
export interface PeekMember {
  id: string;
  name: string;
  emoji: string;
  color: string;
}

/** What a member publishes about themselves, as far as a look reads it. */
interface PublishedMember {
  peerId?: string;
  name?: string;
  emoji?: string;
  color?: string;
  joinedAt?: number;
}

/**
 * Who a presence state says is in the room, one entry per member.
 *
 * A member who re-tracked appears more than once under their key, and the
 * latest entry is what they are now. Members are listed in the order they
 * arrived, so a line naming them does not reshuffle as somebody re-tracks.
 */
export function presentMembers(
  state: Readonly<Record<string, readonly PublishedMember[]>>,
): PeekMember[] {
  const members: Array<PeekMember & { joinedAt: number }> = [];
  for (const [key, entries] of Object.entries(state)) {
    const latest = entries.at(-1);
    if (!latest) continue;
    members.push({
      id: latest.peerId ?? key,
      name: latest.name ?? "",
      emoji: latest.emoji ?? "",
      color: latest.color ?? "",
      joinedAt: Math.min(...entries.map((entry) => entry.joinedAt ?? 0)),
    });
  }
  return members
    .sort((a, b) => a.joinedAt - b.joinedAt)
    .map(({ joinedAt: _joinedAt, ...member }) => member);
}

/** Nothing asked. */
export const UNASKED: RoomPeek = { kind: "unasked" };

/**
 * What the strip says out loud, as a catalogue key and its count — or null
 * where there is nothing worth saying.
 *
 * An empty room gets a sentence of its own rather than a count of nought. A
 * room that answers with nobody in it is not the same as a room that does not
 * answer, and "0 people are at the fire" is a machine saying so.
 */
export function peekLine(
  peek: RoomPeek,
): { key: string; count: number } | null {
  if (peek.kind !== "answered") return null;
  if (peek.count === 0) return { key: "entry.fireUnlit", count: 0 };
  return { key: "entry.atTheFire", count: peek.count };
}
